import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import useWebSocket, { ReadyState } from 'react-use-websocket';
import { ENV } from '../constants';
import { debugLog, debugWarn, debugError } from '../utils/debug';

type JsonSender = (message: unknown) => void;

export type WebSocketContextValue = {
  sendJsonMessage: JsonSender;
  lastMessage: MessageEvent<any> | null;
  readyState: ReadyState;
  myConnectionId: string | null;
};

const noop = () => {};
const defaultValue: WebSocketContextValue = {
  sendJsonMessage: noop,
  lastMessage: null,
  readyState: ReadyState.UNINSTANTIATED,
  myConnectionId: null,
};

const WebSocketContext = createContext<WebSocketContextValue>(defaultValue);

export const WebSocketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const didUnmount = useRef(false);
  const [myConnectionId, setMyConnectionId] = useState<string | null>(null);

  const socketUrl = ENV.WEBSOCKET_URL ?? '';

  debugLog('WS', 'WebSocketProvider mount. URL:', socketUrl || '(未設定)');

  const { sendJsonMessage, lastMessage, readyState } = useWebSocket(
    socketUrl,
    {
      onOpen: (event) => {
        debugLog('WS', '接続確立:', {
          url: socketUrl,
          timestamp: new Date().toISOString(),
          event,
        });
      },
      onClose: (event) => {
        debugWarn('WS', '接続クローズ:', {
          code: event.code,
          reason: event.reason || '(理由なし)',
          wasClean: event.wasClean,
          timestamp: new Date().toISOString(),
        });
      },
      onError: (error) => {
        debugError('WS', 'エラー:', error);
      },
      onMessage: (event) => {
        debugLog('WS', 'メッセージ受信 (raw):', event.data);
        try {
          const parsed = JSON.parse(event.data);
          debugLog('WS', 'メッセージ受信 (parsed):', parsed);
          if (parsed && parsed.type === 'connected' && parsed.connectionId) {
            setMyConnectionId(String(parsed.connectionId));
          }
        } catch {
          // JSON以外のメッセージはそのまま
        }
      },
      shouldReconnect: () => didUnmount.current === false,
      reconnectAttempts: 10,
      reconnectInterval: 3000,
    },
    Boolean(socketUrl)
  );

  // readyState の変化をログ出力
  const readyStateLabel: Record<number, string> = {
    [ReadyState.CONNECTING]: 'CONNECTING',
    [ReadyState.OPEN]: 'OPEN',
    [ReadyState.CLOSING]: 'CLOSING',
    [ReadyState.CLOSED]: 'CLOSED',
    [ReadyState.UNINSTANTIATED]: 'UNINSTANTIATED',
  };
  const prevReadyState = useRef<ReadyState | null>(null);
  useEffect(() => {
    if (prevReadyState.current !== readyState) {
      debugLog(
        'WS',
        `readyState 変化: ${readyStateLabel[prevReadyState.current ?? ReadyState.UNINSTANTIATED] ?? '?'} → ${readyStateLabel[readyState]}`
      );
      prevReadyState.current = readyState;
    }
  });

  useEffect(() => {
    return () => {
      didUnmount.current = true;
    };
  }, []);

  const value: WebSocketContextValue = {
    sendJsonMessage,
    lastMessage,
    readyState,
    myConnectionId,
  };

  return <WebSocketContext.Provider value={value}>{children}</WebSocketContext.Provider>;
};

export function useAppWebSocket(): WebSocketContextValue {
  return useContext(WebSocketContext);
}